import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { billingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { 
  FiDollarSign, 
  FiUsers, 
  FiFileText, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiDownload,
  FiCheckCircle,
  FiRefreshCw,
  FiFilter,
  FiX,
  FiAlertCircle,
  FiTrendingUp,
  FiSave,
  FiMinus,
  FiEye
} from 'react-icons/fi';

const AdminBilling = () => {
  const [activeTab, setActiveTab] = useState('profiles');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  
  // Modal states
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [showAssignProfileModal, setShowAssignProfileModal] = useState(false);
  const [showGenerateBillModal, setShowGenerateBillModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    is_active: true,
    weight_slabs: [{ min_weight: '', max_weight: '', rate: '', currency: 'USD' }]
  });
  
  const [assignmentForm, setAssignmentForm] = useState({
    customer_id: '',
    billing_profile_id: ''
  });
  
  const [billForm, setBillForm] = useState({
    customer_id: '',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    from_date: '',
    to_date: '',
    notes: ''
  });
  
  // Date range filter state
  const [dateRangeFilter, setDateRangeFilter] = useState({
    from_date: '',
    to_date: ''
  });
  
  // Bill detail modal state
  const [showBillDetailModal, setShowBillDetailModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [billBreakdown, setBillBreakdown] = useState(null);
  
  
  
  const queryClient = useQueryClient();

  // Fetch data
  const { data: stats, isLoading: statsLoading } = useQuery(
    ['billing-stats', selectedCustomer],
    () => billingAPI.getStats(selectedCustomer ? { customer_id: selectedCustomer } : {})
  );

  const { data: profiles, isLoading: profilesLoading } = useQuery(
    'billing-profiles',
    () => billingAPI.getProfiles(),
    {
      select: (data) => {
        // Handle paginated response format
        if (data && Array.isArray(data)) {
          return data;
        }
        if (data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      },
      onSuccess: (data) => {
        console.log('BILLING DEBUG: Profiles loaded:', data);
      },
      onError: (error) => {
        console.error('BILLING DEBUG: Error loading profiles:', error);
      }
    }
  );

  const { data: customerAssignments, isLoading: assignmentsLoading } = useQuery(
    'billing-customer-assignments',
    () => billingAPI.getCustomerAssignments(),
    {
      select: (data) => {
        // Handle paginated response format
        if (data && Array.isArray(data)) {
          return data;
        }
        if (data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
    }
  );

  const { data: bills, isLoading: billsLoading } = useQuery(
    ['billing-bills', selectedCustomer, dateRangeFilter],
    () => {
      const params = {};
      if (selectedCustomer) params.customer_id = selectedCustomer;
      if (dateRangeFilter.from_date) params.from_date = dateRangeFilter.from_date;
      if (dateRangeFilter.to_date) params.to_date = dateRangeFilter.to_date;
      
      return billingAPI.getBillsByDateRange(params);
    },
    {
      select: (data) => {
        // Handle paginated response format
        if (data && Array.isArray(data)) {
          return data;
        }
        if (data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
    }
  );

  const { data: unassignedCustomers } = useQuery(
    'billing-unassigned-customers',
    () => billingAPI.getUnassignedCustomers(),
    {
      select: (data) => {
        // Ensure we always return an array
        if (Array.isArray(data)) {
          return data;
        }
        if (data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
    }
  );

  // Mutations
  const createProfileMutation = useMutation(billingAPI.createProfile, {
    onSuccess: () => {
      toast.success('Billing profile created successfully!');
      queryClient.invalidateQueries('billing-profiles');
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
      setShowCreateProfileModal(false);
      resetProfileForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create billing profile');
    },
  });

  const updateProfileMutation = useMutation(
    ({ id, data }) => billingAPI.updateProfile(id, data),
    {
      onSuccess: () => {
        toast.success('Billing profile updated successfully!');
        queryClient.invalidateQueries('billing-profiles');
        queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
        setShowCreateProfileModal(false);
        setEditingProfile(null);
        resetProfileForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update billing profile');
      },
    }
  );

  const deleteProfileMutation = useMutation(billingAPI.deleteProfile, {
    onSuccess: () => {
      toast.success('Billing profile deleted successfully!');
      queryClient.invalidateQueries('billing-profiles');
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete billing profile');
    },
  });

  const assignProfileMutation = useMutation(billingAPI.assignProfile, {
    onSuccess: () => {
      toast.success('Billing profile assigned successfully!');
      queryClient.invalidateQueries('billing-customer-assignments');
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
      setShowAssignProfileModal(false);
      resetAssignmentForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to assign billing profile');
    },
  });

  const generateBillMutation = useMutation(billingAPI.generateBill, {
    onSuccess: () => {
      toast.success('Bill generated successfully!');
      queryClient.invalidateQueries(['billing-bills', selectedCustomer]);
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
      setShowGenerateBillModal(false);
      resetBillForm();
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.error === 'Missing weight slabs') {
        const missingWeights = errorData.missing_weights?.join(', ') || '';
        const profileName = errorData.billing_profile_name || '';
        toast.error(
          `Missing weight slabs for weights: ${missingWeights}. Please add weight slabs for these weights in the "${profileName}" billing profile.`,
          { autoClose: 10000 }
        );
      } else {
        toast.error(errorData?.error || 'Failed to generate bill');
      }
    },
  });

  const markBillPaidMutation = useMutation(billingAPI.markBillPaid, {
    onSuccess: () => {
      toast.success('Bill marked as paid!');
      queryClient.invalidateQueries(['billing-bills', selectedCustomer]);
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to mark bill as paid');
    },
  });

  const recalculateBillMutation = useMutation(billingAPI.recalculateBill, {
    onSuccess: () => {
      toast.success('Bill recalculated successfully!');
      queryClient.invalidateQueries(['billing-bills', selectedCustomer]);
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.error === 'Missing weight slabs') {
        const missingWeights = errorData.missing_weights?.join(', ') || '';
        const profileName = errorData.billing_profile_name || '';
        toast.error(
          `Missing weight slabs for weights: ${missingWeights}. Please add weight slabs for these weights in the "${profileName}" billing profile.`,
          { autoClose: 10000 }
        );
      } else {
        toast.error(errorData?.error || 'Failed to recalculate bill');
      }
    },
  });

  const exportBillMutation = useMutation(billingAPI.exportBill, {
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bill.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Bill exported successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to export bill');
    },
  });



  const approveBillMutation = useMutation(billingAPI.approveBill, {
    onSuccess: () => {
      toast.success('Bill approved successfully!');
      queryClient.invalidateQueries(['billing-bills', selectedCustomer]);
      queryClient.invalidateQueries(['billing-stats', selectedCustomer]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to approve bill');
    },
  });

  const statCards = [
    {
      name: 'Total Profiles',
      value: stats?.total_profiles || 0,
      icon: FiFileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      name: 'Active Profiles',
      value: stats?.active_profiles || 0,
      icon: FiCheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      name: 'Assigned Customers',
      value: stats?.assigned_customers || 0,
      icon: FiUsers,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      name: 'Total Bills',
      value: stats?.total_bills || 0,
      icon: FiDollarSign,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      name: 'Pending Bills',
      value: stats?.pending_bills || 0,
      icon: FiAlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      name: 'Approved Bills',
      value: stats?.approved_bills || 0,
      icon: FiCheckCircle,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      name: 'Paid Bills',
      value: stats?.paid_bills || 0,
      icon: FiCheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
  ];

  const tabs = [
    { id: 'profiles', name: 'Billing Profiles', icon: FiFileText },
    { id: 'assignments', name: 'Customer Assignments', icon: FiUsers },
    { id: 'bills', name: 'Bills', icon: FiDollarSign },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'sent':
        return 'bg-purple-100 text-purple-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleMarkPaid = (billId) => {
    if (window.confirm('Are you sure you want to mark this bill as paid?')) {
      markBillPaidMutation.mutate(billId);
    }
  };

  const handleRecalculate = (billId) => {
    if (window.confirm('Are you sure you want to recalculate this bill? This will update all rates based on the current billing profile.')) {
      recalculateBillMutation.mutate(billId);
    }
  };

  const handleExport = (billId) => {
    exportBillMutation.mutate(billId);
  };

  const handleViewBillDetail = async (bill) => {
    setSelectedBill(bill);
    try {
      const breakdown = await billingAPI.getBillBreakdown(bill.id);
      setBillBreakdown(breakdown);
      setShowBillDetailModal(true);
    } catch (error) {
      toast.error('Failed to load bill details');
    }
  };

  const handleApplyDateFilter = () => {
    // The query will automatically refetch when dateRangeFilter changes
    setShowCustomerFilter(false);
  };

  const handleClearDateFilter = () => {
    setDateRangeFilter({ from_date: '', to_date: '' });
  };



  // Load bill shipment selections

  // Form reset functions
  const resetProfileForm = () => {
    setProfileForm({
      name: '',
      description: '',
      is_active: true,
      weight_slabs: [{ min_weight: '', max_weight: '', rate: '', currency: 'USD' }]
    });
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      customer_id: '',
      billing_profile_id: ''
    });
  };

  const resetBillForm = () => {
    setBillForm({
      customer_id: '',
      bill_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      from_date: '',
      to_date: '',
      notes: ''
    });
  };

  // Profile form handlers
  const handleCreateProfile = () => {
    setEditingProfile(null);
    resetProfileForm();
    setShowCreateProfileModal(true);
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      name: profile.name,
      description: profile.description,
      is_active: profile.is_active,
      weight_slabs: profile.weight_slabs?.length > 0 ? profile.weight_slabs : [{ min_weight: '', max_weight: '', rate: '', currency: 'USD' }]
    });
    setShowCreateProfileModal(true);
  };

  const handleDeleteProfile = (profileId) => {
    if (window.confirm('Are you sure you want to delete this billing profile?')) {
      deleteProfileMutation.mutate(profileId);
    }
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...profileForm,
      weight_slabs: profileForm.weight_slabs.filter(slab => 
        slab.min_weight && slab.max_weight && slab.rate
      )
    };

    if (editingProfile) {
      updateProfileMutation.mutate({ id: editingProfile.id, data });
    } else {
      createProfileMutation.mutate(data);
    }
  };

  const addWeightSlab = () => {
    setProfileForm({
      ...profileForm,
      weight_slabs: [...profileForm.weight_slabs, { min_weight: '', max_weight: '', rate: '', currency: 'USD' }]
    });
  };

  const removeWeightSlab = (index) => {
    if (profileForm.weight_slabs.length > 1) {
      setProfileForm({
        ...profileForm,
        weight_slabs: profileForm.weight_slabs.filter((_, i) => i !== index)
      });
    }
  };

  const updateWeightSlab = (index, field, value) => {
    const updatedSlabs = [...profileForm.weight_slabs];
    updatedSlabs[index] = { ...updatedSlabs[index], [field]: value };
    setProfileForm({ ...profileForm, weight_slabs: updatedSlabs });
  };

  // Assignment form handlers
  const handleAssignProfile = () => {
    resetAssignmentForm();
    setShowAssignProfileModal(true);
  };

  const handleAssignmentSubmit = (e) => {
    e.preventDefault();
    assignProfileMutation.mutate(assignmentForm);
  };

  // Bill form handlers
  const handleGenerateBill = () => {
    resetBillForm();
    setShowGenerateBillModal(true);
  };

  const handleBillSubmit = (e) => {
    e.preventDefault();
    generateBillMutation.mutate(billForm);
  };

  // Draft bill handlers


  const handleApproveBill = (billId) => {
    if (window.confirm('Are you sure you want to approve this bill?')) {
      approveBillMutation.mutate(billId);
    }
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage billing profiles, customer assignments, and generate bills
          </p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center space-x-4">
          {/* Date Range Filter */}
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRangeFilter.from_date}
              onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, from_date: e.target.value })}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="From Date"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRangeFilter.to_date}
              onChange={(e) => setDateRangeFilter({ ...dateRangeFilter, to_date: e.target.value })}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="To Date"
            />
            <button
              onClick={handleApplyDateFilter}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Apply
            </button>
            {(dateRangeFilter.from_date || dateRangeFilter.to_date) && (
              <button
                onClick={handleClearDateFilter}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </div>

          {/* Customer Filter */}
          {selectedCustomer && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Customer: {customerAssignments?.find(c => c.customer.toString() === selectedCustomer)?.customer_name || 'Customer'}
              </span>
              <button
                onClick={() => setSelectedCustomer('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
          )}
          
          <button
            onClick={() => setShowCustomerFilter(!showCustomerFilter)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <FiFilter className="h-4 w-4 mr-2" />
            Filter by Customer
          </button>
        </div>
      </div>

      {/* Customer Filter Dropdown */}
      {showCustomerFilter && (
        <div className="bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Select Customer</h3>
          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
            <button
              onClick={() => {
                setSelectedCustomer('');
                setShowCustomerFilter(false);
              }}
              className={`text-left px-3 py-2 text-sm rounded-md ${
                selectedCustomer === '' 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              All Customers
            </button>
            {customerAssignments && Array.isArray(customerAssignments) && customerAssignments.map((assignment) => (
              <button
                key={assignment.customer}
                onClick={() => {
                  setSelectedCustomer(assignment.customer.toString());
                  setShowCustomerFilter(false);
                }}
                className={`text-left px-3 py-2 text-sm rounded-md ${
                  selectedCustomer === assignment.customer.toString() 
                    ? 'bg-blue-100 text-blue-900' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="font-medium">{assignment.customer_name}</div>
                <div className="text-xs text-gray-500">{assignment.customer_username}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-md ${stat.bgColor}`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Billing Profiles Tab */}
          {activeTab === 'profiles' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Billing Profiles</h3>
                <button 
                  onClick={handleCreateProfile}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiPlus className="h-4 w-4 mr-2" />
                  Create Profile
                </button>
              </div>
              
              {profilesLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="spinner"></div>
                </div>
              ) : profiles?.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Weight Slabs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned Customers
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {profiles && Array.isArray(profiles) && profiles.map((profile) => (
                        <tr key={profile.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                              <div className="text-sm text-gray-500">{profile.description}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {profile.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {profile.weight_slabs?.length || 0} slabs
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {profile.assigned_customers_count || 0} customers
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleEditProfile(profile)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                                title="Edit Profile"
                              >
                                <FiEdit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteProfile(profile.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                title="Delete Profile"
                              >
                                <FiTrash2 className="h-3 w-3 mr-1" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No billing profiles</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new billing profile.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Customer Assignments Tab */}
          {activeTab === 'assignments' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Customer Assignments</h3>
                <button 
                  onClick={handleAssignProfile}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiPlus className="h-4 w-4 mr-2" />
                  Assign Profile
                </button>
              </div>
              
              {assignmentsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="spinner"></div>
                </div>
              ) : customerAssignments?.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Billing Profile
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {customerAssignments.map((assignment) => (
                        <tr key={assignment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{assignment.customer_name}</div>
                              <div className="text-sm text-gray-500">{assignment.customer_username}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {assignment.billing_profile_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {assignment.assigned_by_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(assignment.assigned_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button 
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                                title="Edit Assignment"
                              >
                                <FiEdit className="h-3 w-3 mr-1" />
                                Edit
                              </button>
                              <button 
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                title="Remove Assignment"
                              >
                                <FiTrash2 className="h-3 w-3 mr-1" />
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <FiUsers className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No customer assignments</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Assign billing profiles to customers to start generating bills.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Bills Tab */}
          {activeTab === 'bills' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Bills</h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={handleGenerateBill}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FiPlus className="h-4 w-4 mr-2" />
                    Generate Bill
                  </button>
                </div>
              </div>
              
              {billsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="spinner"></div>
                </div>
              ) : bills?.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bill Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bill Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bills && Array.isArray(bills) && bills.map((bill) => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{bill.bill_number}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{bill.customer_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(bill.status)}`}>
                              {bill.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {bill.currency} {bill.total_amount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(bill.bill_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(bill.due_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex flex-wrap gap-1">
                              {/* Primary Actions - Always visible */}
                              <button
                                onClick={() => handleViewBillDetail(bill)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded hover:bg-purple-200"
                                title="View Bill Details"
                              >
                                <FiEye className="h-3 w-3 mr-1" />
                                View
                              </button>
                              
                              
                              <button
                                onClick={() => handleExport(bill.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                                title="Export Bill"
                              >
                                <FiDownload className="h-3 w-3 mr-1" />
                                Export
                              </button>

                              {/* Status-specific actions */}
                              {bill.status === 'pending_approval' && (
                                <button
                                  onClick={() => handleApproveBill(bill.id)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                                  title="Approve Bill"
                                >
                                  <FiCheckCircle className="h-3 w-3 mr-1" />
                                  Approve
                                </button>
                              )}
                              
                              {bill.status !== 'paid' && (
                                <button
                                  onClick={() => handleMarkPaid(bill.id)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                                  title="Mark as Paid"
                                >
                                  <FiCheckCircle className="h-3 w-3 mr-1" />
                                  Mark Paid
                                </button>
                              )}
                              
                              <button
                                onClick={() => handleRecalculate(bill.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded hover:bg-orange-200"
                                title="Recalculate Bill"
                              >
                                <FiRefreshCw className="h-3 w-3 mr-1" />
                                Recalc
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <FiDollarSign className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No bills</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Generate bills for customers with delivered shipments.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Profile Modal */}
      {showCreateProfileModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingProfile ? 'Edit Billing Profile' : 'Create Billing Profile'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateProfileModal(false);
                    setEditingProfile(null);
                    resetProfileForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Profile Name</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={profileForm.is_active}
                    onChange={(e) => setProfileForm({ ...profileForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active
                  </label>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Weight Slabs</label>
                    <button
                      type="button"
                      onClick={addWeightSlab}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <FiPlus className="h-4 w-4 mr-1" />
                      Add Slab
                    </button>
                  </div>

                  <div className="space-y-2">
                    {profileForm.weight_slabs.map((slab, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="number"
                          placeholder="Min Weight (g)"
                          value={slab.min_weight}
                          onChange={(e) => updateWeightSlab(index, 'min_weight', e.target.value)}
                          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          step="0.01"
                          min="0"
                        />
                        <span className="text-gray-500">-</span>
                        <input
                          type="number"
                          placeholder="Max Weight (g)"
                          value={slab.max_weight}
                          onChange={(e) => updateWeightSlab(index, 'max_weight', e.target.value)}
                          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          step="0.01"
                          min="0"
                        />
                        <input
                          type="number"
                          placeholder="Rate"
                          value={slab.rate}
                          onChange={(e) => updateWeightSlab(index, 'rate', e.target.value)}
                          className="w-24 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          step="0.01"
                          min="0"
                        />
                        <select
                          value={slab.currency}
                          onChange={(e) => updateWeightSlab(index, 'currency', e.target.value)}
                          className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="USD">USD</option>
                          <option value="INR">INR</option>
                          <option value="EUR">EUR</option>
                        </select>
                        {profileForm.weight_slabs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeWeightSlab(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FiMinus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateProfileModal(false);
                      setEditingProfile(null);
                      resetProfileForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createProfileMutation.isLoading || updateProfileMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createProfileMutation.isLoading || updateProfileMutation.isLoading ? (
                      <div className="spinner-sm"></div>
                    ) : (
                      <>
                        <FiSave className="h-4 w-4 mr-2" />
                        {editingProfile ? 'Update' : 'Create'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign Profile Modal */}
      {showAssignProfileModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Assign Billing Profile</h3>
                <button
                  onClick={() => {
                    setShowAssignProfileModal(false);
                    resetAssignmentForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAssignmentSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer</label>
                  <select
                    value={assignmentForm.customer_id}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, customer_id: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Customer</option>
                    {unassignedCustomers && Array.isArray(unassignedCustomers) && unassignedCustomers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name} ({customer.username})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Billing Profile</label>
                  <select
                    value={assignmentForm.billing_profile_id}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, billing_profile_id: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Billing Profile</option>
                    {profiles && Array.isArray(profiles) ? (
                      profiles.filter(p => p.is_active).map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No profiles available</option>
                    )}
                  </select>
                  {/* Debug info */}
                  {profiles && (
                    <div className="mt-1 text-xs text-gray-500">
                      Debug: {Array.isArray(profiles) ? `${profiles.length} profiles loaded` : `Profiles data type: ${typeof profiles}`}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignProfileModal(false);
                      resetAssignmentForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={assignProfileMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {assignProfileMutation.isLoading ? (
                      <div className="spinner-sm"></div>
                    ) : (
                      'Assign Profile'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Generate Bill Modal */}
      {showGenerateBillModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Generate Bill</h3>
                <button
                  onClick={() => {
                    setShowGenerateBillModal(false);
                    resetBillForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleBillSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer</label>
                  <select
                    value={billForm.customer_id}
                    onChange={(e) => {
                      setBillForm({ ...billForm, customer_id: e.target.value });
                    }}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Customer</option>
                    {customerAssignments && Array.isArray(customerAssignments) && customerAssignments.map((assignment) => (
                      <option key={assignment.customer} value={assignment.customer}>
                        {assignment.customer_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Bill Date</label>
                  <input
                    type="date"
                    value={billForm.bill_date}
                    onChange={(e) => setBillForm({ ...billForm, bill_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <input
                    type="date"
                    value={billForm.due_date}
                    onChange={(e) => setBillForm({ ...billForm, due_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">From Date (for bill generation)</label>
                  <input
                    type="date"
                    value={billForm.from_date}
                    onChange={(e) => setBillForm({ ...billForm, from_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">To Date (for bill generation)</label>
                  <input
                    type="date"
                    value={billForm.to_date}
                    onChange={(e) => setBillForm({ ...billForm, to_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={billForm.notes}
                    onChange={(e) => setBillForm({ ...billForm, notes: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional notes for this bill"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowGenerateBillModal(false);
                      resetBillForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={generateBillMutation.isLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {generateBillMutation.isLoading ? (
                      <div className="spinner-sm"></div>
                    ) : (
                      'Generate Bill'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bill Detail Modal */}
      {showBillDetailModal && selectedBill && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Bill Details - {selectedBill.bill_number}
                </h3>
                <button
                  onClick={() => {
                    setShowBillDetailModal(false);
                    setSelectedBill(null);
                    setBillBreakdown(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              {billBreakdown && (
                <div className="space-y-6">
                  {/* Bill Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Bill Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Subtotal:</span>
                        <div className="font-medium">{billBreakdown.currency} {billBreakdown.subtotal.toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Discount:</span>
                        <div className="font-medium">{billBreakdown.currency} {billBreakdown.discount_amount.toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Extra Charges:</span>
                        <div className="font-medium">{billBreakdown.currency} {billBreakdown.extra_charges_total.toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Total:</span>
                        <div className="font-medium text-lg">{billBreakdown.currency} {billBreakdown.total_amount.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Individual Line Items */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Line Items (Child Shipments)</h4>
                    {selectedBill.items && selectedBill.items.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Child Tracking</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Master Tracking</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight Slab</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedBill.items.map((item, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {item.child_tracking_number}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {item.master_tracking_number}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {item.weight}g
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {item.weight_slab_min}g - {item.weight_slab_max}g
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {selectedBill.currency} {item.rate}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                  {selectedBill.currency} {item.amount}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-900">
                                  {item.item_description || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No line items available</p>
                    )}
                  </div>

                  {/* Weight Breakdown */}
                  {billBreakdown.weight_breakdown && billBreakdown.weight_breakdown.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Weight Breakdown</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight Range</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Weight</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Count</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {billBreakdown.weight_breakdown.map((slab, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{slab.weight_range}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{billBreakdown.currency} {slab.rate.toFixed(2)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{slab.total_weight.toFixed(2)}g</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{slab.items_count}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{billBreakdown.currency} {slab.total_amount.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Extra Charges */}
                  {billBreakdown.extra_charges && billBreakdown.extra_charges.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Extra Charges</h4>
                      <div className="space-y-2">
                        {billBreakdown.extra_charges.map((charge, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-yellow-50 rounded-md">
                            <div>
                              <div className="font-medium text-sm">{charge.description}</div>
                              <div className="text-xs text-gray-500">{charge.type.replace('_', ' ')}</div>
                            </div>
                            <div className="font-medium">{billBreakdown.currency} {charge.amount.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Discount Details */}
                  {billBreakdown.discount_amount > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Discount Details</h4>
                      <div className="p-3 bg-green-50 rounded-md">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-sm">Discount Amount: {billBreakdown.currency} {billBreakdown.discount_amount.toFixed(2)}</div>
                            {billBreakdown.discount_percentage > 0 && (
                              <div className="text-xs text-gray-500">Percentage: {billBreakdown.discount_percentage.toFixed(2)}%</div>
                            )}
                            {billBreakdown.discount_reason && (
                              <div className="text-xs text-gray-500">Reason: {billBreakdown.discount_reason}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 mt-6 border-t">
                <button
                  onClick={() => {
                    setShowBillDetailModal(false);
                    setSelectedBill(null);
                    setBillBreakdown(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => handleExport(selectedBill.id)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <FiDownload className="h-4 w-4 mr-2" />
                  Export Bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Draft Bill Modal - Removed */}


      {/* Bill Preview Modal - Removed */}
    </div>
  );
};

export default AdminBilling;
